"use client";
import { createContext, useContext, useEffect, useState } from 'react';
import { 
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { AuthService } from '@/services/auth.service';
import { UserService } from '@/services/user.service';
import { debugLog, debugError } from '@/lib/debug';
import { SignUpFormData, User as AppUser } from '@/types/user';

interface AuthContextType {
  user: User | null;
  appUser: AppUser | null;
  loading: boolean;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signUp: (formData: SignUpFormData, phone: string) => Promise<void>;
  logOut: () => Promise<void>;
  refreshAppUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      debugLog('Auth state changed:', { user: user?.email, loading: false });
      setUser(user);
      
      if (user) {
        // Fetch additional user data from Firestore
        try {
          const userData = await UserService.getUser(user.uid);
          setAppUser(userData);
        } catch (error) {
          debugError('Error fetching user data:', error);
        }
      } else {
        setAppUser(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string, rememberMe: boolean = false) => {
    await AuthService.signIn(email, password, rememberMe);
  };

  const signUp = async (formData: SignUpFormData, phone: string) => {
    await UserService.registerUser(formData, phone);
  };

  const logOut = async () => {
    await AuthService.signOut();
  };

  const refreshAppUser = async () => {
    if (user) {
      try {
        const userData = await UserService.getUser(user.uid);
        setAppUser(userData);
      } catch (error) {
        debugError('Error refreshing user data:', error);
      }
    }
  };

  const value = {
    user,
    appUser,
    loading,
    signIn,
    signUp,
    logOut,
    refreshAppUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
